import React, { FC, useState } from 'react';
import {
    Severity,
    useSystemControllerCreateUserNotificationMutation,
    useSystemControllerDeleteUserNotificationMutation,
    useSystemControllerRetrieveUserNotificationsQuery,
} from '@services/merchantAdminApi';
import LoadingComponent from '@features/common/components/LoadingComponent/LoadingComponent';
import {
    Button,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import Pager from '@features/common/components/Pager/Pager';
import { Box, Grid } from '@mui/system';
import AddIcon from '@mui/icons-material/Add';
import useForm, { FormBase } from '@hooks/useForm';
import { Validators } from '@features/common/helpers/validators';
import ModalDialogComponent from '@features/common/components/ModalDialogComponent/ModalDialogComponent';
import SnackbarUtils from '@features/common/snackbar-utils';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import styles from './NotificationManagementComponent.module.scss';

interface NotificationManagementComponentProps {
}

class CreateNotificationForm extends FormBase<CreateNotificationForm> {
    validators = {
        merchantId: [(v: string) => Validators.isPositiveNumber(v)],
    };

    constructor(
        public merchantId: string,
        public header: string,
        public detail: string,
        public severity: string,
        public path: string,
        public from: string,
        public to: string,
        public action: string,
        public actionText: string,
        public filterFunction: string,
    ) {
        super();
    }

    static create(input: any): CreateNotificationForm {
        return new CreateNotificationForm(
            input.merchantId || '',
            input.header || '',
            input.detail || '',
            input.severity || 'Info',
            input.path || '',
            input.from || '',
            input.to || '',
            input.action || '',
            input.actionText || '',
            input.filterFunction || '',
        );
    }
}

const FILTER_FUNCTIONS = [
    { value: 'requireOtpMfa', label: 'Show only to users without MFA' },
    { value: 'requireNoSso', label: 'Show only to non-SSO users' },
];

const NotificationManagementComponent: FC<NotificationManagementComponentProps> = () => {
    const [notificationsPage, setNotificationsPage] = useState(1);
    const {
        data: notifications,
        isFetching: notificationsFetching,
    } = useSystemControllerRetrieveUserNotificationsQuery({ page: notificationsPage });

    const [createUserNotification, { isLoading: isCreating }] = useSystemControllerCreateUserNotificationMutation();
    const [deleteUserNotification, { isLoading: isDeleting }] = useSystemControllerDeleteUserNotificationMutation();

    const [isAdding, setIsAdding] = useState(false);
    const createForm = useForm(CreateNotificationForm.create({}));

    const handleCreateNotification = async () => {
        const validForm = createForm.isValid();
        if (validForm) {
            const command = {
                notification: {
                    id: 0,
                    merchantId: validForm.merchantId ? parseInt(validForm.merchantId, 10) : null,
                    header: validForm.header,
                    detail: validForm.detail,
                    severity: validForm.severity as Severity,
                    path: validForm.path || null,
                    from: validForm.from || null,
                    to: validForm.to || null,
                    action: validForm.action || null,
                    actionText: validForm.actionText || null,
                    filterFunction: validForm.filterFunction || null,
                },
            };

            await createUserNotification({ createUserNotificationCommand: command }).unwrap();
            setIsAdding(false);
            createForm.setState(CreateNotificationForm.create({}));
            SnackbarUtils.success('Notification created');
        }
    };

    const handleDeleteNotification = async (id: number) => {
        await deleteUserNotification({ id }).unwrap();
        SnackbarUtils.success('Notification deleted');
    };

    const getSelectedFilterFunctions = () => (createForm.value.filterFunction
        ? createForm.value.filterFunction.split(';').filter((f) => f.trim())
        : []);

    const handleFilterFunctionChange = (event: any) => {
        const { value } = event.target;
        const filterString = Array.isArray(value) ? value.join(';') : value;
        createForm.handleFormChange({
            target: { name: 'filterFunction', value: filterString },
        });
    };

    const addNotification = () => {
        setIsAdding(true);
    };

    return (
        <div className={styles.NotificationManagementComponent} data-testid="NotificationManagementComponent">
            <Grid container alignItems="center">
                <Grid size={6}>
                    <Typography variant="h4">Notifications</Typography>
                </Grid>
                <Grid size={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        startIcon={<AddIcon />}
                        disabled={isAdding}
                        onClick={() => addNotification()}
                    >
                        Notification
                    </Button>
                </Grid>

            </Grid>
            <LoadingComponent isLoading={notificationsFetching}>
                <Stack>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Merchant Id</TableCell>
                                    <TableCell>Header</TableCell>
                                    <TableCell>Detail</TableCell>
                                    <TableCell>Severity</TableCell>
                                    <TableCell>Path</TableCell>
                                    <TableCell>From</TableCell>
                                    <TableCell>To</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Action Text</TableCell>
                                    <TableCell>Filter Function</TableCell>
                                    <TableCell>Actions</TableCell>

                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {notifications?.items?.map((n) => (
                                    <TableRow key={n.id}>
                                        <TableCell>{n.merchantId}</TableCell>
                                        <TableCell>{n.header}</TableCell>
                                        <TableCell>{n.detail}</TableCell>
                                        <TableCell>{n.severity}</TableCell>
                                        <TableCell>{n.path}</TableCell>
                                        <TableCell>{n.from}</TableCell>
                                        <TableCell>{n.to}</TableCell>
                                        <TableCell>{n.action}</TableCell>
                                        <TableCell>{n.actionText}</TableCell>
                                        <TableCell>{n.filterFunction}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteNotification(n.id!)}
                                                color="error"
                                                disabled={isDeleting}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pager
                        totalPages={notifications?.totalPages ?? 1}
                        currentPage={notificationsPage}
                        handlePageChange={(event, value) => setNotificationsPage(value)}
                    />
                </Stack>
            </LoadingComponent>
            <ModalDialogComponent
                title="Add Notification"
                open={isAdding}
                setOpen={setIsAdding}
                onConfirm={handleCreateNotification}
                confirmText="Add"
                confirmEnabled={!createForm.hasErrors && !isCreating}
            >
                <Stack spacing={2}>
                    <TextField
                        fullWidth
                        label="Merchant ID"
                        name="merchantId"
                        value={createForm.value.merchantId}
                        onChange={createForm.handleFormChange}
                        helperText={createForm.errors.merchantId?.errorSummary || 'Optional Merchant ID to target'}

                    />
                    <TextField
                        fullWidth
                        label="Header"
                        name="header"
                        value={createForm.value.header}
                        onChange={createForm.handleFormChange}
                        error={!!createForm.errors.header}
                        helperText={createForm.errors.header?.errorSummary || 'Main notification title displayed to users'}
                        required
                    />
                    <TextField
                        fullWidth
                        label="Detail"
                        name="detail"
                        multiline
                        rows={3}
                        value={createForm.value.detail}
                        onChange={createForm.handleFormChange}
                        helperText={createForm.errors.detail?.errorSummary || 'The body of the notification'}
                    />
                    <FormControl fullWidth>
                        <InputLabel>Severity</InputLabel>
                        <Select
                            name="severity"
                            value={createForm.value.severity}
                            onChange={createForm.handleFormChange}
                        >
                            <MenuItem value="Info">Info</MenuItem>
                            <MenuItem value="Warning">Warning</MenuItem>
                            <MenuItem value="Error">Error</MenuItem>
                            <MenuItem value="Success">Success</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label="Path"
                        name="path"
                        value={createForm.value.path}
                        onChange={createForm.handleFormChange}
                        helperText="The URL path under which the notication appears e.g. /scans or /settings"
                    />
                    <TextField
                        fullWidth
                        label="Action"
                        name="action"
                        value={createForm.value.action}
                        onChange={createForm.handleFormChange}
                        helperText="Function name or url for action button"
                    />
                    <TextField
                        fullWidth
                        label="Action Text"
                        name="actionText"
                        value={createForm.value.actionText}
                        onChange={createForm.handleFormChange}
                        helperText="Text to display on the action button"
                    />
                    <FormControl fullWidth>
                        <InputLabel>Filter Functions</InputLabel>
                        <Select
                            multiple
                            name="filterFunction"
                            value={getSelectedFilterFunctions()}
                            onChange={handleFilterFunctionChange}
                            input={<OutlinedInput label="Filter Functions" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const option = FILTER_FUNCTIONS.find((f) => f.value === value);
                                        return <Chip key={value} label={option?.label || value} size="small" />;
                                    })}
                                </Box>
                            )}
                        >
                            {FILTER_FUNCTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </ModalDialogComponent>
        </div>
    );
};

export default NotificationManagementComponent;
